import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  BarChart3, 
  Map, 
  FileText, 
  TrendingUp, 
  Users, 
  Zap, 
  Calendar,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Download,
  Share2
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [searchTerm, setSearchTerm] = useState('');

  // Données mockées
  const projects = [
    {
      id: 1,
      name: 'Villa du Lac - Installation PV',
      client: 'M. et Mme Martin',
      status: 'completed',
      progress: 100,
      power: 6.2,
      production: 7200,
      date: '2024-01-15',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400&h=300&fit=crop'
    },
    {
      id: 2,
      name: 'Entrepôt Logistique Sud',
      client: 'LogiTech Solutions',
      status: 'in-progress',
      progress: 65,
      power: 45.8,
      production: 52000,
      date: '2024-02-20',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop'
    },
    {
      id: 3,
      name: 'Résidence Les Oliviers',
      client: 'Promoteur Immobilier',
      status: 'planning',
      progress: 25,
      power: 12.4,
      production: 14500,
      date: '2024-03-10',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=300&fit=crop'
    }
  ];

  const stats = [
    { title: 'Projets Actifs', value: 12, change: '+3', icon: BarChart3, color: 'from-sky-400 to-sky-600' },
    { title: 'Production Totale', value: '156 MW', change: '+12%', icon: Zap, color: 'from-solar-400 to-solar-600' },
    { title: 'Clients Satisfaits', value: 89, change: '+5%', icon: Users, color: 'from-green-400 to-green-600' },
    { title: 'Revenus Mensuels', value: '€45K', change: '+18%', icon: TrendingUp, color: 'from-purple-400 to-purple-600' }
  ];

  const productionData = {
    labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Production (MWh)',
        data: [65, 78, 92, 85, 98, 112],
        borderColor: '#fde047',
        backgroundColor: 'rgba(253, 224, 71, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const projectTypesData = {
    labels: ['Résidentiel', 'Commercial', 'Industriel', 'Agricole'],
    datasets: [
      {
        data: [45, 25, 20, 10],
        backgroundColor: [
          '#fde047',
          '#38bdf8',
          '#22c55e',
          '#a855f7',
        ],
        borderWidth: 0,
      },
    ],
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in-progress': return 'bg-solar-500';
      case 'planning': return 'bg-sky-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Terminé';
      case 'in-progress': return 'En cours';
      case 'planning': return 'Planification';
      default: return 'Inconnu';
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-display font-bold text-white mb-2">
            Bonjour, {user?.name} 👋
          </h1>
          <p className="text-white/70">
            Voici un aperçu de vos projets photovoltaïques
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="glass-effect rounded-2xl p-6 card-hover"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
                <span className="text-green-400 text-sm font-semibold">{stat.change}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">{stat.value}</h3>
              <p className="text-white/70 text-sm">{stat.title}</p>
            </motion.div>
          ))}
        </div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 mb-8"
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Link to="/project/new" className="button-primary">
              <Plus className="h-5 w-5 mr-2" />
              Nouveau Projet
            </Link>
          </motion.div>
          
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder="Rechercher un projet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="button-secondary"
          >
            <Filter className="h-5 w-5 mr-2" />
            Filtres
          </motion.button>
        </motion.div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Production Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-effect rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Production Mensuelle</h3>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-white text-sm"
              >
                <option value="week">Semaine</option>
                <option value="month">Mois</option>
                <option value="year">Année</option>
              </select>
            </div>
            <div className="h-64">
              <Line
                data={productionData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      labels: { color: 'white' }
                    }
                  },
                  scales: {
                    x: {
                      ticks: { color: 'white' },
                      grid: { color: 'rgba(255,255,255,0.1)' }
                    },
                    y: {
                      ticks: { color: 'white' },
                      grid: { color: 'rgba(255,255,255,0.1)' }
                    }
                  }
                }}
              />
            </div>
          </motion.div>

          {/* Project Types Chart */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-effect rounded-2xl p-6"
          >
            <h3 className="text-xl font-semibold text-white mb-6">Répartition par Type</h3>
            <div className="h-64">
              <Doughnut
                data={projectTypesData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: { color: 'white' }
                    }
                  }
                }}
              />
            </div>
          </motion.div>
        </div>

        {/* Projects List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="glass-effect rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">Projets Récents</h3>
            <Link to="/projects" className="text-solar-400 hover:text-solar-300 text-sm">
              Voir tous les projets
            </Link>
          </div>

          <div className="grid gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                whileHover={{ scale: 1.01 }}
                className="bg-white/5 rounded-xl p-6 border border-white/10"
              >
                <div className="flex items-start space-x-4">
                  <img
                    src={project.image}
                    alt={project.name}
                    className="w-20 h-20 rounded-lg object-cover"
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-1">
                          {project.name}
                        </h4>
                        <p className="text-white/70 text-sm">{project.client}</p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(project.status)}`}>
                          {getStatusText(project.status)}
                        </span>
                        <button className="p-1 hover:bg-white/10 rounded">
                          <MoreVertical className="h-4 w-4 text-white/70" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-white/50 text-xs">Puissance</p>
                        <p className="text-white font-semibold">{project.power} kWc</p>
                      </div>
                      <div>
                        <p className="text-white/50 text-xs">Production</p>
                        <p className="text-white font-semibold">{project.production} kWh/an</p>
                      </div>
                      <div>
                        <p className="text-white/50 text-xs">Progression</p>
                        <p className="text-white font-semibold">{project.progress}%</p>
                      </div>
                      <div>
                        <p className="text-white/50 text-xs">Date</p>
                        <p className="text-white font-semibold">{new Date(project.date).toLocaleDateString('fr-FR')}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center space-x-1 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Voir</span>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center space-x-1 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                        <span>Modifier</span>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center space-x-1 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        <span>PDF</span>
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard; 